import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { HeroFilm, type HeroPhoto } from "@/components/gisela/HeroBand"
import { TextBand, bandBg } from "@/components/gisela/TextBand"
import { BrandMotif } from "@/components/ui/brand-motif"
import { resolveIcon } from "@/components/included-icons"
import { useLocalePath } from "@/lib/locale-path"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import { AccreditationBadges } from "@/components/gisela/AccreditationBadges"
import { CareerTimeline, type TimelineItem } from "@/components/gisela/CareerTimeline"
import { CredentialLists } from "@/components/gisela/CredentialLists"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { OutcomesBlock, type OutcomesContent } from "@/components/gisela/OutcomesBlock"
import { PullQuote, QuoteMark, type PullQuoteData } from "@/components/gisela/PullQuote"
import { SanctuaryBaseBlock, type BaseBlockData } from "@/components/gisela/SanctuaryBaseBlock"
import { TestimonialsBand } from "@/components/gisela/TestimonialsBand"
import { TherapistApproach } from "@/components/gisela/TherapistApproach"
import type { GiselaTherapist, PageMeta, PageProps, PlacementPhoto, PublicTestimonial } from "@/types/inertia"

interface Photo {
  id: number
  url: string | null
  alt_text: string | null
}

interface StandsForItem {
  icon?: string
  title_en?: string
  title_es?: string
  desc_en?: string
  desc_es?: string
}

interface Props {
  therapist: GiselaTherapist | null
  base: BaseBlockData
  pull_quotes: PullQuoteData[]
  content: {
    hero: { eyebrow?: string; heading?: string; subheading?: string; intro?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    story: { heading?: string; body?: string; questions?: string; photo?: Photo }
    timeline: TimelineItem[]
    philosophy: { heading?: string; body?: string; _hidden?: boolean }
    stands_for: StandsForItem[]
    approach: { heading?: string; body?: string; _hidden?: boolean }
    specialisms: { heading?: string; body?: string; _hidden?: boolean }
    outcomes: OutcomesContent
    personal: { heading?: string; body?: string; photo?: Photo; _hidden?: boolean }
    faq: { body?: string }
    // The closing invitation: fixed copy, only its background is editable.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
  }
  /** What her clients say, as on the home page. */
  testimonials?: PublicTestimonial[]
  /** The home page's tile slot — one card per page of her site but this one. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

const PILL = "rounded-full bg-teal px-6 py-2.5 text-sm font-medium text-white no-underline shadow transition-opacity hover:opacity-90"
const CHIP = "inline-flex items-center rounded-full bg-stone-mid px-3.5 py-1.5 text-sm font-medium text-ink-display"

// About Me, after the therapist-profile pages the pro studios build: her
// portrait and essentials first, an at-a-glance strip, then the story with
// its opening questions, and a photo in every other band so a skimming
// reader always has something to look at. Every band is a registry entry
// the admin can reorder; empty ones drop out and cream/stone alternate over
// the rest.
export default function GiselaAbout({
  therapist,
  base,
  pull_quotes,
  content,
  testimonials = [],
  tile_photos = {},
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.about
  const tn = props.translations.gisela_nav
  const lp = useLocalePath()
  const locale = props.current_locale

  const title = meta?.title ?? "About Gisela Ramirez | CBT & EMDR Therapist"
  const description =
    meta?.description ??
    "Gisela Ramirez is a BABCP-accredited Cognitive Behaviour Therapist and EMDR Accredited Practitioner — her story, career, training, how she works and what her clients achieve."

  const standsFor = content.stands_for
    .map((item) => ({
      icon: item.icon ?? "",
      title: (locale === "es" && item.title_es) || item.title_en || "",
      desc: (locale === "es" && item.desc_es) || item.desc_en || "",
    }))
    .filter((item) => item.title !== "")

  const hasCredentials =
    therapist != null &&
    (therapist.qualifications.length > 0 ||
      therapist.accreditations.length > 0 ||
      therapist.training.length > 0)

  // The line under her name is the short version — the bodies she is accredited
  // and registered with. Her degrees are long enough to wrap three times here,
  // so they belong to the Qualifications list further down and nowhere else.
  const heroCredentials =
    therapist == null
      ? []
      : therapist.accreditations.length > 0
        ? therapist.accreditations
        : therapist.qualifications

  const questions = (content.story.questions ?? "")
    .split("\n")
    .map((q) => q.trim())
    .filter((q) => q !== "")

  // Her portrait for the header: the chosen About photo, else the profile
  // portrait, else a sage field so the layout holds.
  const portrait = content.hero.photo?.url
    ? { url: content.hero.photo.url, alt: content.hero.photo.alt_text ?? therapist?.name ?? "" }
    : therapist?.portrait_url
      ? { url: therapist.portrait_url, alt: therapist.name }
      : null

  const bands = presentBands(
    [
      {
        id: "gisela-about-glance",
        present: !!therapist,
        render: (i) => (
          <section className={`${bandBg(i)} py-12`}>
            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
              <div>
                <h4>{t.glance_works_with}</h4>
                <p className="mt-2 text-[15px] text-ink-muted">{t.glance_works_with_value}</p>
              </div>
              <div>
                <h4>{t.glance_sessions}</h4>
                <p className="mt-2 text-[15px] text-ink-muted">
                  {t.glance_online}
                  <br />
                  {t.glance_in_person}
                </p>
              </div>
              {therapist && therapist.specialisms.length > 0 && (
                <div>
                  <h4>{t.glance_focus}</h4>
                  <p className="mt-2 text-[15px] text-ink-muted">{therapist.specialisms.join(" · ")}</p>
                </div>
              )}
              {therapist && therapist.languages.length > 0 && (
                <div>
                  <h4>{t.glance_languages}</h4>
                  <p className="mt-2 text-[15px] text-ink-muted">
                    {therapist.languages.map((lang) => (
                      <React.Fragment key={lang}>
                        {lang}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              )}
            </div>
          </section>
        ),
      },
      {
        id: "gisela-about-story",
        present: !!content.story.body || questions.length > 0,
        render: (i) => (
          <section className={`relative overflow-hidden ${bandBg(i)} py-16 lg:py-20`}>
            <BrandMotif variant="speckle" className="absolute -top-6 -right-10" />
            <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 lg:grid-cols-12 lg:items-center lg:gap-16 lg:px-8">
              <div className={content.story.photo?.url ? "min-w-0 lg:col-span-7" : "min-w-0 lg:col-span-12 max-w-3xl"}>
                {questions.length > 0 ? (
                  <div className="space-y-3">
                    {questions.map((q) => (
                      <p key={q} className="font-display text-2xl italic leading-snug text-teal">
                        {q}
                      </p>
                    ))}
                  </div>
                ) : (
                  <h2>{content.story.heading || t.story_heading}</h2>
                )}
                {content.story.body && (
                  <div className="body-content mt-6 text-ink-body">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.story.body}</ReactMarkdown>
                  </div>
                )}
                {content.hero.intro && <p className="mt-6 text-ink-muted">{content.hero.intro}</p>}
              </div>
              {content.story.photo?.url && (
                <img
                  src={content.story.photo.url}
                  alt={content.story.photo.alt_text ?? ""}
                  className="aspect-[4/3] w-full rounded-2xl object-cover lg:col-span-5"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </div>
          </section>
        ),
      },
      {
        id: "gisela-about-timeline",
        present: content.timeline.length > 0,
        render: (i) => (
          <TextBand index={i} heading={t.experience_heading} className="mt-6 lg:mt-0">
            <CareerTimeline items={content.timeline} locale={locale} />
          </TextBand>
        ),
      },
      {
        id: "gisela-about-credentials-link",
        present: hasCredentials,
        render: (i) => (
          <section className={`${bandBg(i)} py-16`}>
            <div className="mx-auto max-w-6xl px-6 lg:px-8">
              <h2>{t.credentials_heading}</h2>
              <div className="mt-10">{therapist && <CredentialLists therapist={therapist} />}</div>
              <div className="mt-12">
                <AccreditationBadges panel />
              </div>
            </div>
          </section>
        ),
      },
      {
        id: "gisela-about-philosophy",
        present: !sectionHidden(content.philosophy) && !!content.philosophy.body,
        render: (i) => (
          <TextBand index={i} heading={content.philosophy.heading || t.philosophy_heading} body={content.philosophy.body} />
        ),
      },
      {
        id: "gisela-about-stands-for",
        present: standsFor.length > 0,
        render: (i) => (
          <section className={`relative overflow-hidden ${bandBg(i)} py-16`}>
            <BrandMotif variant="speckle" tone="navy-light" opacity={0.8} seed={11} className="absolute -bottom-8 -left-8" />
            <div className="relative mx-auto max-w-6xl px-6 lg:px-8">
              <h2>{t.stands_for_heading}</h2>
              <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {standsFor.map((item) => {
                  // The navy line icons the Sanctuary "What's included" rows
                  // draw, so a commitment card and a venue tile are visibly
                  // the same site. An item that names no icon earns one from
                  // its title.
                  const { Icon, emoji } = resolveIcon(item.icon, item.title)
                  return (
                    <div key={item.title} className="rounded-2xl bg-white p-6 ring-1 ring-hairline">
                      {Icon ? (
                        <Icon aria-hidden className="h-10 w-10 text-teal" strokeWidth={1.25} />
                      ) : (
                        <p className="m-0 text-2xl" aria-hidden="true">{emoji}</p>
                      )}
                      <h3 className="mt-3">{item.title}</h3>
                      {item.desc && <p className="mt-2 mb-0 text-ink-body">{item.desc}</p>}
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        ),
      },
      {
        id: "gisela-about-approach",
        present:
          !sectionHidden(content.approach) &&
          !!therapist &&
          !!(content.approach.body || therapist.approach_cbt || therapist.approach_emdr),
        render: (i) => (
          <TextBand index={i} heading={content.approach.heading || t.approach_heading} body={content.approach.body}>
            {therapist && <TherapistApproach therapist={therapist} />}
            <PullQuote quote={pull_quotes[1]} />
          </TextBand>
        ),
      },
      {
        id: "gisela-about-specialisms",
        present:
          !sectionHidden(content.specialisms) &&
          !!therapist &&
          !!(content.specialisms.body || therapist.specialisms.length > 0),
        render: (i) => (
          <TextBand index={i} heading={content.specialisms.heading || t.who_i_work_with_heading} body={content.specialisms.body}>
            {therapist && therapist.specialisms.length > 0 && (
              <ul className="mt-6 m-0 flex list-none flex-wrap gap-2 p-0">
                {therapist.specialisms.map((s) => (
                  <li key={s} className="rounded-full bg-white px-4 py-1.5 text-sm text-ink-body ring-1 ring-hairline">
                    {s}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-6">
              <a href={lp("/what-i-can-help-with")}>{t.see_everything}</a>
            </p>
          </TextBand>
        ),
      },
      {
        id: "gisela-about-outcomes",
        present: !!content.outcomes.measure,
        render: (i) => (
          <section className={`${bandBg(i)} py-16`}>
            <div className="mx-auto max-w-6xl px-6 lg:px-8">
              <h2>{content.outcomes.heading || t.outcomes_heading}</h2>
              <div className="mt-8">
                <OutcomesBlock outcomes={content.outcomes} />
              </div>
            </div>
          </section>
        ),
      },
      {
        id: "gisela-about-personal",
        present: !sectionHidden(content.personal) && !!content.personal.body,
        render: (i) => (
          <section className={`relative overflow-hidden ${bandBg(i)} py-16 lg:py-20`}>
            <BrandMotif variant="rings" size={480} opacity={0.5} className="absolute -bottom-56 -left-48" />
            <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 lg:grid-cols-12 lg:items-center lg:gap-16 lg:px-8">
              <div className={content.personal.photo?.url ? "min-w-0 lg:col-span-7" : "min-w-0 lg:col-span-12 max-w-3xl"}>
                <h2>{content.personal.heading || t.personal_heading}</h2>
                <div className="body-content mt-6 text-ink-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.personal.body ?? ""}</ReactMarkdown>
                </div>
              </div>
              {content.personal.photo?.url && (
                <img
                  src={content.personal.photo.url}
                  alt={content.personal.photo.alt_text ?? therapist?.name ?? ""}
                  className="aspect-[4/5] w-full rounded-2xl object-cover shadow-lg lg:col-span-5"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </div>
          </section>
        ),
      },
      {
        id: "gisela-about-base",
        present: true,
        render: (i) => <SanctuaryBaseBlock base={base} className={bandBg(i)} />,
      },
      {
        id: "gisela-about-quote",
        present: !!pull_quotes[0],
        render: () => (
          <section className="relative overflow-hidden bg-teal py-16 text-white lg:py-20">
            <BrandMotif variant="rings" tone="white" size={760} opacity={0.3} className="absolute -top-80 -right-72" />
            <figure className="relative mx-auto m-0 max-w-4xl px-6 text-center lg:px-8">
              <QuoteMark className="mx-auto mb-5 h-7 w-7 text-white/60" />
              <blockquote className="m-0 border-0 p-0">
                <p className="m-0 font-display text-2xl italic leading-snug text-white sm:text-3xl">
                  {pull_quotes[0].quote}
                </p>
              </blockquote>
              <figcaption className="mt-5">
                <cite className="text-xs font-medium uppercase tracking-[0.12em] text-white/70 not-italic">
                  {pull_quotes[0].attribution}
                </cite>
              </figcaption>
            </figure>
          </section>
        ),
      },
      {
        id: "gisela-about-faq",
        present: !!content.faq.body,
        render: (i) => <TextBand index={i} heading={t.faq_heading} body={content.faq.body} />,
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* ── Header: portrait, name, essentials ─────────────────────── */}
        {!sectionHidden(content.hero) && (
          <section className="relative overflow-hidden bg-stone pt-24 pb-16 lg:pt-28 lg:pb-20">
            <BrandMotif variant="rings" size={700} className="absolute -top-64 -left-72" />
            <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
              <div className="lg:col-span-5">
                {/* A portrait is a disc on this brand — the home hero's, and
                    the mark's own rings. The crescent is the same circle
                    offset up and left, so only a sliver of the line clears
                    the photo; on stone it is drawn in brand navy rather than
                    the white the hero uses over a photograph. */}
                <div className="relative mx-auto aspect-square w-full max-w-md lg:mx-0">
                  <div
                    aria-hidden
                    className="absolute -top-[3.4%] -left-[4.5%] h-full w-full rounded-full border-2 border-teal-light"
                  />
                  {portrait ? (
                    <img
                      src={portrait.url}
                      alt={portrait.alt}
                      className="relative h-full w-full rounded-full object-cover object-top shadow-lg"
                      fetchPriority="high"
                    />
                  ) : (
                    <div aria-hidden className="relative h-full w-full rounded-full bg-sage-light shadow-lg" />
                  )}
                </div>
                {/* Under the portrait, the centre she founded — the chip beside
                    her name says it in words; this is the mark itself. */}
                <a href={props.brand_urls.sanctuary} className="mx-auto mt-8 block w-40 lg:mx-0">
                  <img src="/logos/sanctuary-lockup.png" alt="Sanctuary Therapy Centre" className="h-auto w-full" loading="lazy" decoding="async" />
                </a>
              </div>
              <div className="lg:col-span-7">
                <p className="mb-3 text-xs font-medium uppercase tracking-[0.14em] text-ink-muted">
                  {content.hero.eyebrow || t.hero_eyebrow}
                </p>
                <h1>{content.hero.heading || therapist?.name || t.hero_heading}</h1>
                {heroCredentials.length > 0 && (
                  <h4 className="mt-3 text-teal">{heroCredentials.join(" · ")}</h4>
                )}
                {content.hero.subheading && <p className="mt-5 max-w-xl text-lg leading-relaxed">{content.hero.subheading}</p>}
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className={CHIP}>{t.chip_online_in_person}</span>
                  <span className={CHIP}>{t.chip_founder}</span>
                  {therapist?.languages.map((lang) => (
                    <span key={lang} className={CHIP}>
                      {lang}
                    </span>
                  ))}
                </div>
                <div className="mt-7">
                  <a href={lp("/book")} className={PILL}>
                    {tn.book_call}
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}

        {/* ── The close, as on the home page: the teal invitation, the tiles
               out of it, then what her clients say. The words stay this
               page's own — someone who has just read about her is deciding
               about her, not browsing. ──────────────────────────────────── */}
        {!sectionHidden(content.cta) && (
          <section className="relative overflow-hidden bg-teal py-16 text-white lg:py-20">
            <HeroFilm photo={content.cta?.photo} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
              <h2 className="text-white">{t.cta_heading}</h2>
              <p className="mt-3 text-white/75">{t.cta_body}</p>
              <div className="mt-7">
                <a
                  href={lp("/book")}
                  className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                >
                  {tn.book_call}
                </a>
              </div>
            </div>
          </section>
        )}

        {/* This page's own tile is left out — the reader is on it. */}
        <GiselaPageTiles photos={tile_photos} exclude={["/about"]} />

        {props.features.testimonials_display && <TestimonialsBand testimonials={testimonials} />}
      </PublicLayout>
    </>
  )
}
